import { useMemo, useState } from "react";
import {
  Archive,
  ChevronDown,
  ChevronRight,
  FolderTree,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import { DropdownField, FormField } from "@/components/FormField";
import { generateId, useApp, type Container } from "@/context/AppContext";

/**
 * Container-tree manager (TNK-187, epic TNK-186). Renders the arbitrary-depth
 * storage hierarchy (site → case/shelf → bin → box …) and lets the operator
 * add / nest / rename / re-parent / archive nodes. Additive: bins + locations
 * created through the existing UI are mirrored in here (kind=bin/site); the new
 * nesting levels (case/shelf/box) live only as containers.
 *
 * Phase A organizes the *containers*; per-card placement into deep nodes comes
 * with the read-flip phase, so this view doesn't move cards yet.
 */

const KIND_OPTIONS = [
  { value: "site", label: "Site / Store" },
  { value: "case", label: "Display case" },
  { value: "shelf", label: "Shelf" },
  { value: "binder", label: "Binder" },
  { value: "bin", label: "Bin" },
  { value: "box", label: "Box" },
  { value: "tab", label: "Tab / divider" },
];
const KIND_LABEL: Record<string, string> = {
  site: "Site",
  case: "Case",
  shelf: "Shelf",
  binder: "Binder",
  bin: "Bin",
  box: "Box",
  tab: "Tab",
  unsorted: "Unsorted",
};

type FormState =
  | { mode: "add"; parentId: string | null }
  | { mode: "edit"; id: string }
  | null;

export function ContainerTreePanel() {
  const {
    containers,
    addContainer,
    updateContainer,
    archiveContainer,
    restoreContainer,
    deleteContainer,
  } = useApp();

  const [open, setOpen] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [banner, setBanner] = useState<{
    kind: "error" | "success";
    text: string;
  } | null>(null);
  const [form, setForm] = useState<FormState>(null);
  const [fName, setFName] = useState("");
  const [fKind, setFKind] = useState("bin");
  const [fParent, setFParent] = useState<string>("");

  const visible = useMemo(
    () => containers.filter((c) => showArchived || !c.archivedAt),
    [containers, showArchived],
  );

  // Group visible nodes by parent. A node whose parent isn't visible renders as
  // a root so nothing silently disappears.
  const childrenOf = useMemo(() => {
    const ids = new Set(visible.map((c) => c.id));
    const m = new Map<string | null, Container[]>();
    for (const c of visible) {
      const key = c.parentId && ids.has(c.parentId) ? c.parentId : null;
      const arr = m.get(key) ?? [];
      arr.push(c);
      m.set(key, arr);
    }
    for (const arr of m.values()) {
      arr.sort(
        (a, b) => a.kind.localeCompare(b.kind) || a.name.localeCompare(b.name),
      );
    }
    return m;
  }, [visible]);

  const descendantsOf = (id: string): Set<string> => {
    const out = new Set<string>();
    const walk = (pid: string) => {
      for (const c of containers) {
        if (c.parentId === pid && !out.has(c.id)) {
          out.add(c.id);
          walk(c.id);
        }
      }
    };
    walk(id);
    return out;
  };

  const childCount = (id: string) =>
    containers.filter((c) => c.parentId === id).length;

  // Parent options exclude self + descendants when editing (cycle guard; the
  // server enforces it too).
  const parentOptions = useMemo(() => {
    const exclude =
      form?.mode === "edit"
        ? new Set([form.id, ...descendantsOf(form.id)])
        : new Set<string>();
    return [
      { value: "", label: "— Top level —" },
      ...containers
        .filter((c) => !c.archivedAt && !exclude.has(c.id))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({
          value: c.id,
          label: `${KIND_LABEL[c.kind] ?? c.kind}: ${c.name}`,
        })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containers, form]);

  function openAdd(parentId: string | null) {
    setForm({ mode: "add", parentId });
    setFName("");
    setFKind(parentId ? "box" : "site");
    setFParent(parentId ?? "");
    setBanner(null);
  }
  function openEdit(c: Container) {
    setForm({ mode: "edit", id: c.id });
    setFName(c.name);
    setFKind(c.kind);
    setFParent(c.parentId ?? "");
    setBanner(null);
  }

  function submit() {
    const name = fName.trim();
    if (!name) {
      setBanner({ kind: "error", text: "Name is required." });
      return;
    }
    if (!form) return;
    if (form.mode === "add") {
      addContainer({
        id: generateId("CTN"),
        kind: fKind,
        name,
        parentId: fParent || undefined,
        state: "open",
        createdAt: new Date().toISOString(),
      });
      setBanner({
        kind: "success",
        text: `Added ${KIND_LABEL[fKind] ?? fKind} "${name}".`,
      });
    } else {
      const existing = containers.find((c) => c.id === form.id);
      if (!existing) return;
      updateContainer({
        ...existing,
        name,
        kind: fKind,
        parentId: fParent || undefined,
      });
      setBanner({ kind: "success", text: "Updated." });
    }
    setForm(null);
  }

  async function doArchive(id: string) {
    const r = await archiveContainer(id);
    setBanner(
      r.error
        ? { kind: "error", text: r.error }
        : { kind: "success", text: "Archived." },
    );
  }
  async function doRestore(id: string) {
    const r = await restoreContainer(id);
    setBanner(
      r.error
        ? { kind: "error", text: r.error }
        : { kind: "success", text: "Restored." },
    );
  }
  async function doDelete(id: string) {
    const r = await deleteContainer(id);
    setBanner(
      r.error
        ? { kind: "error", text: r.error }
        : { kind: "success", text: "Deleted." },
    );
  }

  function toggle(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const roots = childrenOf.get(null) ?? [];

  function renderNode(node: Container, depth: number) {
    const kids = childrenOf.get(node.id) ?? [];
    const isCollapsed = collapsed.has(node.id);
    const cnt = childCount(node.id);
    return (
      <div key={node.id}>
        <div
          className="group flex items-center gap-2 rounded-md py-1.5 pr-2 hover:bg-muted/50"
          style={{ paddingLeft: depth * 18 + 8 }}
        >
          {kids.length > 0 ? (
            <button
              type="button"
              onClick={() => toggle(node.id)}
              className="text-muted-foreground hover:text-foreground"
              aria-label={isCollapsed ? "Expand" : "Collapse"}
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          ) : (
            <span className="inline-block w-4" />
          )}
          {node.color ? (
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ background: node.color }}
            />
          ) : null}
          <span className="rounded border border-border px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {KIND_LABEL[node.kind] ?? node.kind}
          </span>
          <span className="text-sm font-medium text-foreground">
            {node.name}
          </span>
          {node.archivedAt && (
            <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
              archived
            </span>
          )}
          {cnt > 0 && (
            <span className="text-[11px] text-muted-foreground">· {cnt}</span>
          )}
          <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100">
            <button
              type="button"
              title="Add inside"
              onClick={() => openAdd(node.id)}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Edit / move"
              onClick={() => openEdit(node)}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            {node.archivedAt ? (
              <button
                type="button"
                title="Restore"
                onClick={() => void doRestore(node.id)}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                type="button"
                title="Archive"
                onClick={() => void doArchive(node.id)}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Archive className="h-3.5 w-3.5" />
              </button>
            )}
            {cnt === 0 && (
              <button
                type="button"
                title="Delete"
                onClick={() => void doDelete(node.id)}
                className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        {!isCollapsed && kids.map((k) => renderNode(k, depth + 1))}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        <FolderTree className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold text-foreground">
          Storage tree
        </span>
        <span className="text-xs text-muted-foreground">
          nest sites, cases, shelves, bins, boxes
        </span>
        {open ? (
          <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div className="space-y-3 border-t border-border px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => openAdd(null)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              <Plus className="h-4 w-4" />
              Add container
            </button>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="h-3.5 w-3.5 accent-primary"
              />
              Show archived
            </label>
          </div>

          {banner && (
            <div
              className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
                banner.kind === "success"
                  ? "border-emerald-600 bg-emerald-100 text-emerald-700"
                  : "border-destructive bg-destructive/10 text-destructive"
              }`}
            >
              <div className="flex-1">{banner.text}</div>
              <button
                type="button"
                onClick={() => setBanner(null)}
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {form && (
            <div className="space-y-2 rounded-md border border-border bg-background p-3">
              <div className="text-sm font-semibold text-foreground">
                {form.mode === "add" ? "New container" : "Edit container"}
              </div>
              <FormField
                label="Name"
                value={fName}
                onChangeText={setFName}
                placeholder="e.g. Display Case A, Box 3"
                required
              />
              <DropdownField
                label="Type"
                value={fKind}
                options={KIND_OPTIONS}
                onChange={setFKind}
              />
              <DropdownField
                label="Inside"
                value={fParent}
                options={parentOptions}
                onChange={setFParent}
              />
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setForm(null)}
                  className="rounded-md bg-muted px-3 py-1.5 text-sm font-semibold text-muted-foreground hover:bg-muted/70"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={!fName.trim()}
                  className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
                >
                  {form.mode === "add" ? "Add" : "Save"}
                </button>
              </div>
            </div>
          )}

          {roots.length === 0 ? (
            <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
              No containers yet. Add a site or case to start the tree.
            </div>
          ) : (
            <div className="rounded-md border border-border">
              {roots.map((r) => renderNode(r, 0))}
            </div>
          )}

          <p className="text-[11px] text-muted-foreground">
            Bins + sites you create on this page appear here automatically.
            Placing individual cards into deep containers comes in a later phase
            — for now this organizes the structure.
          </p>
        </div>
      )}
    </div>
  );
}
